import React from 'react';
import styled from 'styled-components';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { formatCurrency } from 'src/utils/misc';
import { WebGlobeSvg, FormulaSvg, InfoCircleSvg } from 'src/assets/dynamicSvg';
import { SuggestedImpact, IntelVariable } from '../types';
import { QuantNote } from './QuantNote';
import { IntelFormulaView } from './IntelFormulaView';
import { Collapsible } from './Collapsible';
import { LabelChip } from './LabelChip';

const Card = styled.div<{ $first?: boolean }>`
  ${({ theme, $first }) => !$first && `border-top: 1px solid ${theme.colors.stroke};`}
`;

const HeaderZone = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 20px 24px;
`;

const RangeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const Money = styled(Poppins)`
  background: ${({ theme }) => theme.colors.gradient};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

// Shown in place of the £ range when a referenced variable is unpopulated (screen-17).
const MissingBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.dropZoneErrBg};
  border: 1px solid ${({ theme }) => theme.colors.dropZoneErrBorder};
  border-radius: 8px;
`;

const DetailZone = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 20px 40px;
`;

const BoundBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 16px;
`;

const SourceRow = styled.div<{ $first?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 16px;
  ${({ theme, $first }) => !$first && `border-top: 1px solid ${theme.colors.stroke};`}
`;

const SourceTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
`;

interface ImpactFormulaCardProps {
  impact: SuggestedImpact;
  variables: IntelVariable[];
  first?: boolean;
  onVariableClick?: (variableId: number, anchorEl: HTMLElement) => void;
}

export const ImpactFormulaCard: React.FC<ImpactFormulaCardProps> = ({ impact, variables, first, onVariableClick }) => {
  const { formula } = impact;
  if (!formula) return null;

  const computed = Number.isFinite(formula.computedLower) && Number.isFinite(formula.computedUpper);

  return (
    <Card $first={first}>
      <HeaderZone>
        <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
          {impact.name}
        </Poppins>

        <QuantNote note={formula.note} />

        {computed ? (
          <RangeRow>
            <Poppins px={14} color="cflowerBlue">
              From
            </Poppins>
            <Money px={18} weight={600}>
              {formatCurrency(formula.computedLower as number)}
            </Money>
            <Poppins px={14} color="cflowerBlue">
              to
            </Poppins>
            <Money px={18} weight={600}>
              {formatCurrency(formula.computedUpper as number)}
            </Money>
          </RangeRow>
        ) : (
          <MissingBanner>
            <InfoCircleSvg size={20} color="#E13D3D" style={{ flexShrink: 0, marginTop: 1 }} />
            <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
              Your workspace is missing variables required to calculate this value — add the missing variables below
              to see the amount.
            </Poppins>
          </MissingBanner>
        )}
      </HeaderZone>

      <DetailZone>
        <Collapsible icon={<FormulaSvg size={16} />} label="Formula">
          <BoundBlock>
            <Poppins px={14} weight={500} color="prussianBlue">
              Lower Bound:
            </Poppins>
            <IntelFormulaView raw={formula.lowerRaw} variables={variables} onVariableClick={onVariableClick} />
          </BoundBlock>
          <BoundBlock>
            <Poppins px={14} weight={500} color="prussianBlue">
              Upper Bound:
            </Poppins>
            <IntelFormulaView raw={formula.upperRaw} variables={variables} onVariableClick={onVariableClick} />
          </BoundBlock>
        </Collapsible>

        <Collapsible icon={<WebGlobeSvg size={14} />} label="Sources">
          <div>
            {formula.sources.map((source, idx) => (
              <SourceRow key={source.id} $first={idx === 0}>
                <SourceTitle>
                  <WebGlobeSvg size={14} />
                  <Poppins px={14} weight={500} color="prussianBlue">
                    {source.title}
                  </Poppins>
                </SourceTitle>
                <Pills>
                  {source.industry && <LabelChip filled label="Industry" value={source.industry} />}
                  {source.weight != null && <LabelChip filled label="Weight" value={`${source.weight}%`} />}
                </Pills>
                {source.quote && (
                  <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
                    {`“${source.quote}”`}
                  </Poppins>
                )}
              </SourceRow>
            ))}
          </div>
        </Collapsible>
      </DetailZone>
    </Card>
  );
};
